<script>
  import { createEventDispatcher } from 'svelte';
  import supabase from '../shared/supabaseClient'; // Import Supabase client

  export let user; // User object passed as prop, contains email
  export let isPremium; // Premium status prop

  const dispatch = createEventDispatcher();

  let selectedOption = 'paste'; // 'paste' or 'fetch'
  let pastedJobDescription = '';
  let fetchedJobDescriptions = []; // Store fetched job descriptions
  let isLoadingJobs = false;
  let fetchError = null;
  let isAnalyzing = false;
  let analysisError = null;
  // Store the raw analysis result AND the parsed version
  let rawAnalysisResult = null;
  let parsedAnalysis = null; // { beforeTable: string, table: { headers: [], rows: [] }, afterTable: string } | null

  function closeModal() {
    dispatch('close');
  }

  async function fetchLastAppliedJobs() {
    if (!user) return;
    isLoadingJobs = true;
    fetchError = null;
    try {
      const { data, error } = await supabase
        .from('extracted_texts')
        .select('job_description')
        .ilike('user_email', user.email) // Case-insensitive match
        .order('id', { ascending: false }) // Most recent first
        .limit(5);
      if (error) throw error;
      fetchedJobDescriptions = data.map(item => item.job_description);
    } catch (error) {
      console.error("Error fetching job descriptions:", error);
      fetchError = error.message;
      fetchedJobDescriptions = [];
    } finally {
      isLoadingJobs = false;
    }
  }

  $: if (selectedOption === 'fetch' && fetchedJobDescriptions.length === 0 && !isLoadingJobs) {
    fetchLastAppliedJobs();
  }

  async function handleAnalyze() {
    if (!isPremium) return;

    isAnalyzing = true;
    analysisError = null;
    rawAnalysisResult = null;
    parsedAnalysis = null; // Reset parsed data

    const body = selectedOption === 'paste'
      ? { option: 'paste', jobDescription: pastedJobDescription }
      : { option: 'fetch' };

    try {
      const response = await fetch('/skills/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
          const errorBody = await response.text();
          console.error('Analysis API Error Response Body:', errorBody); // Log error body
          throw new Error(`Analysis failed with status ${response.status}. Check console for details.`);
      }
      const { analysis } = await response.json();
      rawAnalysisResult = analysis; // Store raw result
      // --- DEBUGGING: Log raw analysis before parsing ---
      console.log('Raw Analysis Received:', analysis);
      parsedAnalysis = parseAnalysisResponse(analysis); // Parse it
      // --- DEBUGGING: Log parsed analysis ---
      console.log('Parsed Analysis Result:', JSON.stringify(parsedAnalysis, null, 2));

    } catch (error) {
      console.error('Analysis error during fetch or parsing:', error);
      analysisError = error.message;
    } finally {
      isAnalyzing = false;
    }
  }

  // Function to render non-table markdown content (Keep your existing improved version)
  function renderSimpleMarkdown(text) {
    if (!text) return '';

    let html = text;

    // Headers (# H1, ## H2, etc.) - Start from H6 up to H1
    html = html.replace(/^######\s+(.*$)/gim, '<h6>$1</h6>');
    html = html.replace(/^#####\s+(.*$)/gim, '<h5>$1</h5>');
    html = html.replace(/^####\s+(.*$)/gim, '<h4>$1</h4>');
    html = html.replace(/^###\s+(.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^##\s+(.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^#\s+(.*$)/gim, '<h1>$1</h1>');

    // Bold (**text**)
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
    // Italic (*text*) - Careful with list markers
    html = html.replace(/(?<!\*)\*(?!\*|\s)(.*?)(?<!\s)\*(?!\*)/gim, '<em>$1</em>');

    // Optional: Treat **Header:** as h3 (keep if needed)
    html = html.replace(/^\*\*(.*?):\*\*/gim, '<h3>$1</h3>');

    // Unordered lists (* text or - text)
    html = html.replace(/^\s*[\*\-]\s+(.*$)/gim, '<li>$1</li>');
    // Wrap consecutive LIs in ULs (this part can be complex, basic version shown)
    html = html.replace(/<\/li>\s*<li>/g, '</li><li>'); // Compact LIs
    // Wrap blocks of LIs if they are not already wrapped (this might need more refinement)
    html = html.replace(/(<li>.*?<\/li>)(?!\s*<\/ul>)/gs, (match, p1) => {
      // Avoid wrapping if already in a list or contains other major blocks
      if (match.includes('<ul') || match.includes('<h')) return match;
      // Check if the *previous* line ended a list item properly
      const precedingText = html.substring(0, html.indexOf(match));
      if (!precedingText.match(/<\/li>\s*$/) && !precedingText.match(/<ul>\s*$/)) {
        return `<ul>${p1}</ul>`; // Wrap if it starts a new list block
      }
      return p1; // Assume it continues an existing list
    });
    // Attempt to close open ULs if followed by non-LI content or end of text
    html = html.replace(/<\/li>(?!\s*<li>)/gs, '</li></ul>');
    // Cleanup potentially wrongly inserted empty lists or lists around headers
    html = html.replace(/<ul>\s*<\/ul>/g, '');
    html = html.replace(/<ul>\s*(<h[1-6]>.*?<\/h[1-6]>)\s*<\/ul>/gim, '$1');

    // Paragraphs: Wrap lines not part of other blocks in <p> tags.
    // Split by double newline, then wrap non-empty, non-block lines.
    const blocks = html.split(/\n\s*\n/); // Split by one or more empty lines
    let processedHtml = blocks.map(block => {
        const trimmedBlock = block.trim();
        if (!trimmedBlock) return ''; // Skip empty blocks
        // Check if the block already starts with a known block element
        if (trimmedBlock.match(/^<(h[1-6]|ul|ol|li|table|thead|tbody|tr|th|td)/i)) {
            return block; // Return as is (potentially with original spacing)
        }
        // Wrap in paragraph, convert single newlines to <br> within the paragraph
        return `<p>${trimmedBlock.replace(/\n/g, '<br>')}</p>`;
    }).join('\n\n'); // Re-join with double newlines

    // --- Final Cleanups (Keep these) ---
    processedHtml = processedHtml.replace(/<p>\s*<\/p>/g, ''); // Remove empty paragraphs
    processedHtml = processedHtml.replace(/<p>\s*(<(h[1-6]|ul|ol|table))/g, '$1'); // Remove <p> before block elements
    processedHtml = processedHtml.replace(/(<\/(h[1-6]|ul|ol|table)>)\s*<\/p>/g, '$1'); // Remove </p> after block elements
    // Add specific cleanup for lists if needed
    processedHtml = processedHtml.replace(/<p>\s*(<ul>.*?<\/ul>)\s*<\/p>/gs, '$1');
    processedHtml = processedHtml.replace(/<p>\s*(<h[1-6]>.*?<\/h[1-6]>)\s*<\/p>/gs, '$1');
    processedHtml = processedHtml.replace(/\s+<\/p>/g, '</p>'); // Trim space before closing p
    processedHtml = processedHtml.replace(/<br\s*\/?>\s*<\/p>/gi, '</p>'); // Remove breaks just before closing p
    processedHtml = processedHtml.replace(/<p>\s*<br\s*\/?>/gi, '<p>'); // Remove breaks just after opening p

    return processedHtml.trim(); // Trim final output
  }


  // *** REVISED Function to parse the specific Gemini response format ***
  function parseAnalysisResponse(markdownText) {
      if (!markdownText) return null;

      const lines = markdownText.trim().split('\n');
      let beforeTable = [];
      let tableLines = [];
      let afterTable = [];
      let inTable = false;
      let headerLine = null; // Store potential header line
      let separatorLineFound = false;

      // Regex to detect the table separator line (e.g., |---|:---|---| or |:---|:---:|---:|)
      // Allows spaces, colons, and dashes between pipes. Requires at least 3 dashes somewhere.
      const separatorRegex = /^\|?[\s:-]*[-]{3,}[\s:-]*\|(?:[\s:-]*[-]{3,}[\s:-]*\|)+[\s:-]*\|?$/;

      // Regex to detect a potential table row (starts and ends with |, contains pipes inside)
      const rowRegex = /^\|.*\|$/;
      const contentRowRegex = /^\|.*\|.*\|/; // Check for at least two pipes for content

      for (const line of lines) {
          const trimmedLine = line.trim();

          // Ignore empty lines for parsing logic, but preserve them in output sections
          if (!trimmedLine) {
              if (inTable) afterTable.push(line); // Preserve empty lines after table
              else if (tableLines.length === 0) beforeTable.push(line); // Preserve empty lines before table start
              else afterTable.push(line); // Treat as ending the table if empty line occurs after rows started
              inTable = false; // Empty line usually signifies end of table block
              continue;
          }

          // Check if it's the separator line
          if (!separatorLineFound && headerLine && separatorRegex.test(trimmedLine)) {
              // console.log(`DEBUG: Separator FOUND: "${trimmedLine}"`);
              separatorLineFound = true;
              inTable = true; // Officially in the table now
              tableLines.push(headerLine); // Add the stored header line
              // We don't add the separator line itself to tableLines
              continue; // Move to the next line
          }

          // Check if it looks like a table row / header
          if (rowRegex.test(trimmedLine) && contentRowRegex.test(trimmedLine)) {
              // console.log(`DEBUG: Potential Row/Header: "${trimmedLine}"`);
              if (inTable) {
                  // Already found separator, this is a data row
                  tableLines.push(trimmedLine);
              } else if (!separatorLineFound && !headerLine) {
                  // First potential row line, assume it's the header for now
                  headerLine = trimmedLine;
              } else if (!separatorLineFound && headerLine) {
                  // Found another row-like line before separator - this isn't a simple table.
                  // Assume the previous 'header' was just text. Reset and treat as beforeTable.
                  // console.log(`DEBUG: False header alarm. Resetting.`);
                  beforeTable.push(headerLine);
                  beforeTable.push(trimmedLine); // Add current line too
                  headerLine = null; // Reset header
              } else {
                 // Row format, but state is weird (e.g., after table ended) - treat as afterTable
                 afterTable.push(line);
              }
          } else {
              // Not a separator or a table row
              // console.log(`DEBUG: Regular Text: "${trimmedLine}"`);
              if (inTable) {
                  // Was in a table, but this line isn't part of it. Table ends here.
                  inTable = false;
                  afterTable.push(line);
              } else if (headerLine && !separatorLineFound) {
                  // Had a potential header, but this line breaks the pattern before a separator.
                  // Assume the header was just text.
                   // console.log(`DEBUG: Header candidate followed by non-row/non-sep. Resetting.`);
                  beforeTable.push(headerLine);
                  beforeTable.push(line); // Add current line
                  headerLine = null; // Reset header
              }
              else {
                  // Regular text, goes to beforeTable if table hasn't started, afterTable otherwise
                  (separatorLineFound || afterTable.length > 0 ? afterTable : beforeTable).push(line);
              }
          }
      }

      // --- Process collected table lines ---
      let headers = [];
      let rows = [];
      if (tableLines.length > 0) {
          // Extract headers from the first line using slice to remove empty start/end elements
          headers = tableLines[0].split('|').slice(1, -1).map(h => h.trim());

          // Extract rows from subsequent lines using slice
          rows = tableLines.slice(1).map(rowLine => {
              return rowLine.split('|').slice(1, -1).map(cell => cell.trim());
          });
           // console.log("DEBUG: Parsed Headers:", headers);
           // console.log("DEBUG: Parsed Rows:", rows);
      } else {
          // No valid table structure found (header + separator + optional rows)
          // If we had a header candidate but no separator, add it back to beforeTable
          if (headerLine) {
             beforeTable.push(headerLine);
          }
          // Combine everything into beforeTable if no table was successfully parsed
          beforeTable.push(...afterTable);
          afterTable = [];
           // console.log("DEBUG: No valid table structure parsed.");
      }


      return {
          beforeTable: beforeTable.join('\n'),
          table: { headers, rows },
          afterTable: afterTable.join('\n'),
      };
  }

  // Function to handle printing the analysis results (Keep your existing version)
  function handlePrint() {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');

    // Generate a clean printable version of the analysis
    // Ensure parsedAnalysis exists and has the expected structure
    if (!parsedAnalysis) {
      console.error("Cannot print, analysis data is not available.");
      printWindow.close();
      return;
    }

    // Ensure table data is valid before trying to map over it
    const hasTable = parsedAnalysis.table &&
                     parsedAnalysis.table.headers &&
                     parsedAnalysis.table.headers.length > 0 &&
                     parsedAnalysis.table.rows; // Check rows array exists

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Skills Analysis Report</title>
          <style>
            body { font-family: Arial, sans-serif; color: #333; line-height: 1.6; padding: 20px; max-width: 800px; margin: 0 auto; }
            h1 { color: #2a4b7c; margin-bottom: 20px; text-align: center; border-bottom: 2px solid #2a4b7c; padding-bottom: 10px; }
            h2, h3 { color: #2a4b7c; margin-top: 20px; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; vertical-align: top; } /* Added vertical-align */
            th { background-color: #eef2f7; color: #2a4b7c; font-weight: 600; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            ul { margin-bottom: 15px; padding-left: 25px; list-style: disc; } /* Ensure list style */
            li { margin-bottom: 5px; }
            p { margin-bottom: 1em; } /* Ensure paragraph spacing */
            .timestamp { font-size: 0.85em; color: #777; margin-top: 30px; text-align: right; font-style: italic; }
            @media print {
              body { padding: 0; margin: 0; }
              h1, h2, h3 { page-break-after: avoid; } /* Avoid breaks after headers */
              table, ul, p { page-break-inside: avoid; } /* Avoid breaking tables/lists/paragraphs */
            }
          </style>
        </head>
        <body>
          <h1>Skills Analysis Report</h1>

          <!-- Before table content -->
          ${parsedAnalysis.beforeTable ? renderSimpleMarkdown(parsedAnalysis.beforeTable) : ''}

          <!-- Table content -->
          ${
            hasTable
            ? `
              <!-- Removed redundant H2, assuming H2/H3 might be in beforeTable -->
              <table>
                <thead>
                  <tr>
                    ${parsedAnalysis.table.headers.map(header => `<th>${header}</th>`).join('')}
                  </tr>
                </thead>
                <tbody>
                  ${parsedAnalysis.table.rows.map(row => `
                    <tr>
                      ${row.map(cell => `<td>${renderSimpleMarkdown(cell)}</td>`).join('')}
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : '<p><em>No structured missing skills table was identified in the analysis.</em></p>'
          }

          <!-- After table content -->
          ${parsedAnalysis.afterTable ? renderSimpleMarkdown(parsedAnalysis.afterTable) : ''}

          <div class="timestamp">Generated on: ${new Date().toLocaleString()}</div>
        </body>
      </html>
    `;

    // Write to the new window and print
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load then print
    // Use setTimeout to ensure rendering completes in the new window
    setTimeout(() => {
        printWindow.print();
        // Optional: close after printing attempt, but usually better to leave open
        // printWindow.close();
    }, 500); // Wait 500ms before printing
  }

</script>

<div class="modal-backdrop" on:click|self={closeModal}>
  <div class="modal-content skills-analyzer-modal">
    <button class="modal-close-button" on:click={closeModal}>×</button>
    <h2>Skills Analyzer</h2>
    {#if !isPremium}
      <p class="error">This feature requires a premium subscription.</p>
      <p>Upgrade your account to analyze job descriptions against your skills.</p>
    {:else}
      <p>Analyze a job description against your saved resume and profile.</p>

      <div class="input-options">
        <label>
          <input type="radio" bind:group={selectedOption} value="paste">
          Paste Job Description
        </label>
        <label>
          <input type="radio" bind:group={selectedOption} value="fetch">
          Use Last 5 Applied Job Descriptions
        </label>
      </div>

      {#if selectedOption === 'paste'}
        <div>
          <label for="pasted-jd">Paste Job Description Below:</label>
          <textarea id="pasted-jd" bind:value={pastedJobDescription} rows="10" placeholder="Paste the full job description here..."></textarea>
        </div>
      {:else if selectedOption === 'fetch'}
        <div class="fetch-area">
          <label>Using Job Descriptions from Database:</label>
          {#if isLoadingJobs}
            <p>Loading recent job descriptions...</p>
          {:else if fetchError}
            <p class="error">Error loading jobs: {fetchError}</p>
          {:else if fetchedJobDescriptions.length > 0}
            <ul class="fetched-jobs-list">
              {#each fetchedJobDescriptions as jd, i}
                <li><strong>Job {i + 1}:</strong> {jd.substring(0, 100)}{jd.length > 100 ? '...' : ''}</li>
              {/each}
            </ul>
          {:else}
            <p>No recent job descriptions found or unable to load.</p>
          {/if}
          <textarea id="pasted-jd-disabled" rows="10" placeholder="Job description fetched from your application history." disabled></textarea>
        </div>
      {/if}

      <div class="modal-buttons">
        <button
          on:click={handleAnalyze}
          disabled={
            (selectedOption === 'paste' && !pastedJobDescription.trim()) ||
            (selectedOption === 'fetch' && fetchedJobDescriptions.length === 0) ||
            isLoadingJobs ||
            isAnalyzing
          }
          class:analyzing={isAnalyzing}
        >
          {#if isAnalyzing}
            <span class="analyzing-text">Analyzing...</span>
            <div class="button-spinner"></div>
          {:else}
            Analyze Skills
          {/if}
        </button>
        <button class="cancel-button" on:click={closeModal} disabled={isAnalyzing}>Cancel</button>
      </div>

      {#if analysisError}
        <p class="error analysis-error-message">Analysis failed: {analysisError}</p>
      {/if}

      {#if isAnalyzing && !parsedAnalysis && !analysisError}
        <div class="analysis-loading">
           <p>Analyzing your skills against job requirements...</p>
           <div class="spinner">
             <div class="bounce1"></div>
             <div class="bounce2"></div>
             <div class="bounce3"></div>
           </div>
           <p class="loading-subtext">This may take a moment.</p>
         </div>
      {/if}

      {#if parsedAnalysis && !isAnalyzing}
        <div class="analysis-results">
          <!-- Print button for analysis results -->
          <div class="print-button-container">
            <button class="print-button" on:click={handlePrint}>
              <span class="print-icon">🖨️</span> Print Results
            </button>
          </div>
          
          <!-- Render content before the table -->
          {#if parsedAnalysis.beforeTable}
             <div class="analysis-section">
               {@html renderSimpleMarkdown(parsedAnalysis.beforeTable)}
             </div>
          {/if}

          <!-- Render the table if it exists -->
          {#if parsedAnalysis.table && parsedAnalysis.table.headers.length > 0 && parsedAnalysis.table.rows.length > 0}
            <div class="analysis-section analysis-table-section">
               <h3>Missing Skills Report</h3> <!-- Add title explicitly if parser doesn't grab it -->
               <div class="table-container">
                 <table>
                   <thead>
                     <tr>
                       {#each parsedAnalysis.table.headers as header}
                         <th>{header}</th>
                       {/each}
                     </tr>
                   </thead>
                   <tbody>
                     {#each parsedAnalysis.table.rows as row}
                       <tr>
                         {#each row as cell, i}
                           <td>
                             <!-- Render cell content - apply markdown for lists inside cells -->
                             {@html renderSimpleMarkdown(cell)}
                           </td>
                         {/each}
                       </tr>
                     {/each}
                   </tbody>
                 </table>
               </div>
             </div>
          {/if}

          <!-- Render content after the table -->
          {#if parsedAnalysis.afterTable}
             <div class="analysis-section">
               {@html renderSimpleMarkdown(parsedAnalysis.afterTable)}
             </div>
           {/if}
        </div>
      {/if}

    {/if} <!-- end of isPremium check -->
  </div>
</div>

<style>
  /* --- Existing Styles (Minor adjustments possible) --- */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-content {
    background: #f8f6f2; /* Slightly warmer background */
    border-radius: 8px;
    padding: 25px 35px; /* Adjust padding */
    max-width: 900px; /* Increased from 750px to provide more width */
    width: 95%; /* Increased from 90% to use more screen space */
    text-align: left;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    position: relative;
    max-height: 90vh; /* Increase max height */
    overflow-y: auto;
    border: 1px solid #e0dccc;
  }

  .skills-analyzer-modal h2 {
    color: #2a4b7c;
    margin-top: 0;
    margin-bottom: 20px;
    text-align: center;
    font-weight: 600;
  }

  .skills-analyzer-modal p {
    margin-bottom: 15px;
    line-height: 1.6;
    color: #444;
  }

  .modal-close-button {
    position: absolute;
    top: 15px;
    right: 15px;
    background: none;
    border: none;
    font-size: 28px;
    color: #999;
    cursor: pointer;
    padding: 5px;
    line-height: 1;
    transition: color 0.2s ease;
  }
  .modal-close-button:hover {
    color: #333;
  }

  .input-options {
    margin-bottom: 20px;
    display: flex;
    gap: 25px;
    border-bottom: 1px solid #e0dccc;
    padding-bottom: 15px;
  }

  .input-options label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: #333;
  }
   .input-options input[type="radio"] {
     accent-color: #2a4b7c;
   }


  textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
    margin-top: 8px;
    resize: vertical;
    box-sizing: border-box; /* Include padding in width */
    background-color: #fff;
     min-height: 150px;
  }

  textarea:disabled {
    background-color: #f0f0f0;
    cursor: not-allowed;
    color: #888;
  }
   label[for="pasted-jd"], .fetch-area label {
     font-weight: 600;
     color: #333;
     font-size: 0.95em;
   }


  .fetch-area {
    margin-top: 10px;
  }

  .fetch-area label {
    display: block;
    margin-bottom: 10px;
  }

  .fetched-jobs-list {
    list-style: none;
    padding: 0;
    margin-bottom: 10px;
    max-height: 150px;
    overflow-y: auto;
    border: 1px dashed #ccc;
    padding: 10px 15px;
    border-radius: 4px;
    background-color: #fdfcf9;
    font-size: 0.9em;
  }
  .fetched-jobs-list li {
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
    line-height: 1.4;
  }
  .fetched-jobs-list li:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
   .fetched-jobs-list strong {
     color: #2a4b7c;
   }

  .modal-buttons {
    margin-top: 25px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 15px;
    border-top: 1px solid #e0dccc;
  }

  .modal-buttons button {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 500;
    transition: background-color 0.2s ease, opacity 0.2s ease;
    display: inline-flex; /* For spinner alignment */
    align-items: center; /* For spinner alignment */
    justify-content: center; /* For spinner alignment */
    gap: 8px; /* Space between text and spinner */
    min-width: 100px; /* Ensure minimum width */
  }

  .modal-buttons button:disabled:not(.analyzing) {
     background-color: #ccc;
     cursor: not-allowed;
     opacity: 0.7;
   }

   /* Primary analyze button */
   .modal-buttons button:first-of-type {
       background-color: #2a4b7c;
       color: white;
   }
   .modal-buttons button:first-of-type:hover:not(:disabled) {
       background-color: #1e3a66;
   }


  .modal-buttons button.cancel-button {
    background-color: #aaa;
    color: white;
  }
  .modal-buttons button.cancel-button:hover:not(:disabled) {
    background-color: #888;
  }
   .modal-buttons button.cancel-button:disabled {
     background-color: #ccc;
     cursor: not-allowed;
     opacity: 0.7;
   }


  .error {
    color: #c0392b; /* Darker red */
    font-weight: 500; /* Slightly less bold */
    background-color: #f9e0de;
    border: 1px solid #f1c0ba;
    padding: 10px 15px;
    border-radius: 4px;
    margin-bottom: 15px;
  }
   .analysis-error-message {
     margin-top: 20px;
   }

  /* --- Loading States --- */
   .analysis-loading {
     margin-top: 30px;
     text-align: center;
     padding: 30px 20px;
     background-color: #f8f6f2;
     border-radius: 5px;
     border: 1px dashed #e0dccc;
   }
   .analysis-loading p {
     margin-bottom: 15px;
     color: #555;
     font-size: 1.05em;
   }
   .analysis-loading .loading-subtext {
     font-size: 0.9em;
     color: #777;
     margin-top: 15px;
     margin-bottom: 0;
   }

   /* Button loading spinner */
   .button-spinner {
     width: 16px;
     height: 16px;
     border: 2px solid rgba(255, 255, 255, 0.3);
     border-radius: 50%;
     border-top-color: #fff;
     animation: spin 1s linear infinite;
   }
   button.analyzing .analyzing-text {
      margin-right: 8px; /* Add space if needed, handled by gap */
   }


  /* Main page loading spinner styles */
  .spinner {
    margin: 0 auto 15px auto; /* Center and add margin */
    width: 60px; /* Smaller spinner */
    text-align: center;
  }

  .spinner > div {
    width: 15px; /* Smaller dots */
    height: 15px;
    background-color: #2a4b7c;
    border-radius: 100%;
    display: inline-block;
    animation: bounce-delay 1.4s infinite ease-in-out both;
  }

  .spinner .bounce1 { animation-delay: -0.32s; }
  .spinner .bounce2 { animation-delay: -0.16s; }

  @keyframes bounce-delay {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1.0); }
  }

  @keyframes spin {
     to { transform: rotate(360deg); }
   }


  /* --- Analysis Results Styling --- */
  .analysis-results {
    margin-top: 25px;
    padding: 20px;
    background-color: #ffffff; /* White background for results */
    border-radius: 6px;
    border: 1px solid #e0dccc;
    width: 100%; /* Ensure full width */
    box-sizing: border-box; /* Include padding in width calculation */
  }

  .analysis-section {
    margin-bottom: 25px;
    width: 100%;
    overflow-wrap: break-word; /* Allow long words to break */
    word-wrap: break-word; /* For older browsers */
  }
  .analysis-section:last-child {
    margin-bottom: 0;
  }

  /* Global styles for rendered markdown content */
  .analysis-results :global(h3) {
    color: #2a4b7c;
    margin-top: 1.5em;
    margin-bottom: 0.8em;
    font-size: 1.3em; /* Slightly larger */
    font-weight: 600;
    padding-bottom: 5px;
    border-bottom: 1px solid #eee;
  }
   .analysis-results .analysis-section:first-child :global(h3:first-child) {
      margin-top: 0; /* No top margin for the very first header */
   }


  .analysis-results :global(p) {
    margin-bottom: 1em;
    line-height: 1.6;
    color: #333;
  }

  .analysis-results :global(ul) {
    margin-bottom: 1em;
    padding-left: 1.8em; /* Indent lists */
    list-style: disc; /* Use standard disc */
  }
   /* Nested lists */
   .analysis-results :global(ul ul) {
       margin-top: 0.5em;
       margin-bottom: 0.5em;
       list-style: circle; /* Different style for nested */
   }


  .analysis-results :global(li) {
    margin-bottom: 0.5em; /* Space between list items */
    line-height: 1.5;
  }

  .analysis-results :global(strong) {
    font-weight: 600; /* Slightly less bold than default bold */
    color: #111;
  }

  .analysis-results :global(em) {
    font-style: italic;
    color: #444;
  }

  /* --- Table Specific Styling --- */
   .analysis-table-section h3 {
       margin-bottom: 15px; /* More space before table */
       border-bottom: none; /* Remove border if title is separate */
   }
  .table-container {
    overflow-x: auto; /* Ensure table scrolls horizontally if needed */
    margin-top: 10px;
    width: 100%; /* Ensure full width */
  }

  .analysis-results table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1.5em;
    font-size: 0.95em; /* Slightly smaller table font */
    table-layout: auto; /* Let browser decide column widths */
  }

  .analysis-results th,
  .analysis-results td {
    border: 1px solid #ddd;
    padding: 10px 12px; /* More padding */
    text-align: left;
    vertical-align: top; /* Align content to top */
    line-height: 1.5;
  }

  .analysis-results th {
    background-color: #eef2f7; /* Light blue-gray header */
    font-weight: 600;
    color: #2a4b7c;
    white-space: nowrap; /* Prevent headers from wrapping */
  }

  .analysis-results tbody tr:nth-child(odd) {
    background-color: #f9f9f9; /* Subtle row striping */
  }
   .analysis-results tbody tr:hover {
     background-color: #f1f1f1; /* Hover effect */
   }

  /* Ensure lists inside table cells look good */
  .analysis-results td :global(ul) {
     margin-top: 5px;
     margin-bottom: 5px;
     padding-left: 1.5em; /* Adjust list indent inside cells */
   }
   .analysis-results td :global(li) {
       margin-bottom: 3px;
   }
   .analysis-results td :global(p) {
       margin-bottom: 0.5em;
   }

  /* Print button styles */
  .print-button-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 15px;
  }
  
  .print-button {
    background-color: #4a6da7;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background-color 0.2s ease;
  }
  
  .print-button:hover {
    background-color: #3a5a8c;
  }
  
  .print-icon {
    font-size: 16px;
  }
  
  /* Hide print button in print view */
  @media print {
    .print-button-container,
    .modal-buttons,
    .modal-close-button,
    .input-options {
      display: none !important;
    }
  }

</style>